import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Collapse, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Ban,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  Columns3,
  Loader,
  Pencil,
} from "lucide-react";

import { Progress } from "../../components/Progress";
import { DocumentationLinkList } from "../../components/DocumentationLinkList";
import { Badge } from "../../components/Badge";
import { BADGE_TONES } from "../../theme/badgeTones";
import { brand } from "../../theme/theme";

// Tonos de estado compartidos por fases, tareas y el propio setup (los del Badge)
const TONES = BADGE_TONES;

const PHASE_STATUS = {
  completed: { label: "Completada", tone: "done" },
  in_progress: { label: "En curso", tone: "active" },
  not_started: { label: "Pendiente", tone: "pending" },
  no_necesario: { label: "No necesaria", tone: "muted" },
};

const TASK_STATUS = {
  finalizado: { label: "Completada", tone: "done" },
  completed: { label: "Completada", tone: "done" },
  en_desarrollo: { label: "En curso", tone: "active" },
  planeado: { label: "Pendiente", tone: "muted" },
  blocked: { label: "Bloqueada", tone: "blocked" },
  no_necesario: { label: "No necesaria", tone: "muted" },
};

const SETUP_STATUS = {
  completed: { label: "Completado", tone: "done" },
  in_progress: { label: "En curso", tone: "active" },
  not_started: { label: "No iniciado", tone: "pending" },
  no_necesario: { label: "No necesario", tone: "muted" },
  cancelled: { label: "Cancelado", tone: "blocked" },
};

const PHASE_STATUS_OPTIONS = ["not_started", "in_progress", "completed", "no_necesario"];
const TASK_STATUS_OPTIONS = ["planeado", "en_desarrollo", "finalizado", "no_necesario"];

const DONE_TASK_STATUSES = new Set(["finalizado", "completed"]);

const getTaskStatus = (status) => TASK_STATUS[status] || TASK_STATUS.planeado;

const formatStartDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("es-ES", { day: "numeric", month: "long" });
  } catch {
    return null;
  }
};

const cardSx = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "16px",
});

/**
 * Pill/texto de estado. Si recibe `options` + `onChange` se convierte en un selector
 * (menú desplegable) para poder cambiar el estado desde la propia fila.
 */
function StatusSelect({ value, statusMap, options, onChange, variant = "pill", pillBackground }) {
  const status = statusMap[value] || statusMap[options?.[0]] || { label: value, tone: "muted" };
  const tone = TONES[status.tone];
  const isEditable = Boolean(onChange && options?.length);

  const variantSx =
    variant === "text"
      ? {
        px: 0.75,
        py: 0.5,
        borderRadius: "8px",
        backgroundColor: "transparent",
        ...(isEditable && { "&:hover": { backgroundColor: tone.background } }),
      }
      : pillBackground
        ? { backgroundColor: pillBackground }
        : {};

  return (
    <Badge
      color={status.tone}
      selector={isEditable}
      value={value}
      options={options?.map((option) => ({
        value: option,
        label: statusMap[option].label,
        color: statusMap[option].tone,
      }))}
      onChange={onChange}
      sx={variantSx}
    >
      {status.label}
    </Badge>
  );
}

StatusSelect.propTypes = {
  value: PropTypes.string,
  statusMap: PropTypes.object.isRequired,
  options: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(["pill", "text"]),
  pillBackground: PropTypes.string,
};

function IconTile({ size = 36, radius = "10px", tone, background, children }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: radius,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: TONES[tone]?.color,
        backgroundColor: background || TONES[tone]?.background,
      }}
    >
      {children}
    </Box>
  );
}

IconTile.propTypes = {
  size: PropTypes.number,
  radius: PropTypes.string,
  tone: PropTypes.string,
  background: PropTypes.string,
  children: PropTypes.node,
};

const TASK_ICONS = {
  done: Check,
  active: Loader,
  blocked: Clock,
  muted: Circle,
};

function SetupTaskRow({ task, phaseId, isClient, onUpdateTaskStatus, onEditTask }) {
  const theme = useTheme();
  const status = getTaskStatus(task.status);
  const isBlocked = task.status === "blocked";
  const TaskIcon = task.status === "no_necesario" ? Ban : TASK_ICONS[status.tone] || Circle;
  const canEdit = !isClient && onEditTask && task.status !== "no_necesario";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.5,
        borderTop: `1px solid ${theme.palette.divider}`,
        "&:first-of-type": { borderTop: "none" },
        "&:hover .setup-task-edit": { opacity: 1 },
        opacity: task.status === "no_necesario" ? 0.65 : 1,
      }}
    >
      <IconTile size={28} radius="8px" tone={status.tone}>
        <TaskIcon size={14} />
      </IconTile>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 500,
            color: "text.primary",
            lineHeight: 1.3,
            textDecoration: task.status === "no_necesario" ? "line-through" : "none",
          }}
        >
          {task.title}
        </Typography>
        {task.description ? (
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: isBlocked ? TONES.blocked.color : "text.secondary",
              lineHeight: 1.35,
              mt: 0.25,
              wordBreak: "break-word",
            }}
          >
            {task.description}
          </Typography>
        ) : null}
      </Box>

      {!isClient ? <DocumentationLinkList urls={task.documentation_urls} /> : null}

      <Typography
        sx={{
          display: { xs: "none", sm: "block" },
          fontSize: "0.8125rem",
          color: "text.secondary",
          whiteSpace: "nowrap",
          maxWidth: 140,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {task.assigned_to_name || (isClient ? "" : "Sin asignar")}
      </Typography>

      {canEdit ? (
        <Tooltip title="Editar tarea" arrow>
          <IconButton
            className="setup-task-edit"
            size="small"
            onClick={() => onEditTask(task)}
            sx={{ width: 28, height: 28, opacity: { xs: 1, md: 0.45 }, transition: "opacity 0.15s" }}
          >
            <Pencil size={14} />
          </IconButton>
        </Tooltip>
      ) : null}

      <Box sx={{ minWidth: { sm: 104 }, display: "flex", justifyContent: "flex-end" }}>
        <StatusSelect
          variant="text"
          value={task.status}
          statusMap={TASK_STATUS}
          options={isClient || !onUpdateTaskStatus ? undefined : TASK_STATUS_OPTIONS}
          onChange={
            isClient || !onUpdateTaskStatus
              ? undefined
              : (nextStatus) => onUpdateTaskStatus(phaseId, task.id, nextStatus)
          }
        />
      </Box>
    </Box>
  );
}

SetupTaskRow.propTypes = {
  task: PropTypes.object.isRequired,
  phaseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isClient: PropTypes.bool,
  onUpdateTaskStatus: PropTypes.func,
  onEditTask: PropTypes.func,
};

function SetupPhaseRow({
  phase,
  index,
  isExpanded,
  onToggle,
  isClient,
  onUpdatePhaseStatus,
  onUpdateTaskStatus,
  onEditTask,
  onEditPhase,
  onViewPhaseKanban,
}) {
  const theme = useTheme();
  const PhaseIcon = phase.icon;

  const visibleTasks = useMemo(
    () =>
      isClient
        ? (phase.tasks || []).filter((task) => task.status !== "no_necesario")
        : phase.tasks || [],
    [isClient, phase.tasks]
  );
  const countableTasks = (phase.tasks || []).filter((task) => task.status !== "no_necesario");
  const doneTasks = countableTasks.filter((task) => DONE_TASK_STATUSES.has(task.status)).length;

  // Fase abierta: cabecera en rosa suave de marca, como en el diseño
  const highlighted = isExpanded;
  const titleColor = highlighted ? brand.pinkDark : theme.palette.text.primary;

  const tileProps =
    phase.status === "completed"
      ? { tone: "done", icon: <Check size={18} /> }
      : phase.status === "no_necesario"
        ? { tone: "muted", icon: <Ban size={18} /> }
        : highlighted
          ? { tone: "active", background: theme.palette.background.paper, icon: <PhaseIcon size={18} /> }
          : phase.status === "in_progress"
            ? { tone: "active", icon: <PhaseIcon size={18} /> }
            : { tone: "muted", icon: <PhaseIcon size={18} /> };

  return (
    <Box
      sx={{
        ...cardSx(theme),
        overflow: "hidden",
        borderColor: highlighted ? brand.softBorder : theme.palette.divider,
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: highlighted ? `0 6px 20px -12px ${alpha(theme.palette.primary.main, 0.35)}` : "none",
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.25, sm: 2 },
          px: { xs: 1.5, sm: 2.5 },
          py: 1.75,
          cursor: "pointer",
          backgroundColor: highlighted ? brand.soft : "transparent",
          transition: "background-color 0.2s",
          "&:hover": { backgroundColor: highlighted ? brand.soft : theme.palette.action.hover },
        }}
      >
        <Box sx={{ display: "flex", color: highlighted ? brand.pinkDark : "text.secondary" }}>
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </Box>

        <IconTile tone={tileProps.tone} background={tileProps.background}>
          {tileProps.icon}
        </IconTile>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "1.125rem", fontWeight: 450, color: titleColor, lineHeight: 1.3 }}>
            Fase {index + 1} · {phase.title}
          </Typography>
          {phase.description ? (
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: "text.secondary",
                lineHeight: 1.35,
                mt: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {phase.description}
            </Typography>
          ) : null}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.5, sm: 1 }}
          onClick={(event) => event.stopPropagation()}
          sx={{ flexShrink: 0 }}
        >
          {onViewPhaseKanban ? (
            <Tooltip title="Ver fase en kanban" arrow>
              <IconButton
                size="small"
                onClick={() => onViewPhaseKanban(phase.id)}
                sx={{ width: 30, height: 30, display: { xs: "none", sm: "inline-flex" } }}
              >
                <Columns3 size={15} />
              </IconButton>
            </Tooltip>
          ) : null}
          {!isClient && onEditPhase ? (
            <Tooltip title="Editar fase" arrow>
              <IconButton
                size="small"
                onClick={() => onEditPhase(phase)}
                sx={{ width: 30, height: 30, display: { xs: "none", sm: "inline-flex" } }}
              >
                <Pencil size={15} />
              </IconButton>
            </Tooltip>
          ) : null}

          {countableTasks.length > 0 ? (
            <Typography
              sx={{
                display: { xs: "none", sm: "block" },
                fontSize: "0.875rem",
                fontWeight: 500,
                color: highlighted ? brand.pinkDark : "text.secondary",
                whiteSpace: "nowrap",
                px: 0.5,
              }}
            >
              {doneTasks} / {countableTasks.length}
            </Typography>
          ) : null}

          <StatusSelect
            value={phase.status}
            statusMap={PHASE_STATUS}
            options={isClient || !onUpdatePhaseStatus ? undefined : PHASE_STATUS_OPTIONS}
            onChange={
              isClient || !onUpdatePhaseStatus
                ? undefined
                : (nextStatus) => onUpdatePhaseStatus(phase.id, nextStatus)
            }
            pillBackground={highlighted ? theme.palette.background.paper : undefined}
          />
        </Stack>
      </Box>

      <Collapse in={isExpanded} timeout={250} unmountOnExit>
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pl: { sm: 3 }, py: 0.5 }}>
          {visibleTasks.length > 0 ? (
            visibleTasks.map((task) => (
              <SetupTaskRow
                key={task.id}
                task={task}
                phaseId={phase.id}
                isClient={isClient}
                onUpdateTaskStatus={onUpdateTaskStatus}
                onEditTask={onEditTask}
              />
            ))
          ) : (
            <Typography sx={{ py: 2, fontSize: "0.875rem", color: "text.secondary" }}>
              Esta fase no tiene tareas.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

SetupPhaseRow.propTypes = {
  phase: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  isClient: PropTypes.bool,
  onUpdatePhaseStatus: PropTypes.func,
  onUpdateTaskStatus: PropTypes.func,
  onEditTask: PropTypes.func,
  onEditPhase: PropTypes.func,
  onViewPhaseKanban: PropTypes.func,
};

function SetupProgressCard({ phases, projectProgress, setupState, startDate }) {
  const theme = useTheme();
  const setupStatus = setupState ? SETUP_STATUS[setupState] : null;

  const stats = useMemo(() => {
    const allTasks = phases.flatMap((phase) => phase.tasks || []);
    const countable = allTasks.filter((task) => task.status !== "no_necesario");
    return {
      completedPhases: phases.filter((phase) => phase.status === "completed").length,
      done: countable.filter((task) => DONE_TASK_STATUSES.has(task.status)).length,
      blocked: countable.filter((task) => task.status === "blocked").length,
      pending: countable.filter(
        (task) => !DONE_TASK_STATUSES.has(task.status) && task.status !== "blocked"
      ).length,
    };
  }, [phases]);

  const startLabel = formatStartDate(startDate);
  const statItems = [
    { value: stats.done, label: stats.done === 1 ? "tarea hecha" : "tareas hechas" },
    { value: stats.pending, label: stats.pending === 1 ? "pendiente" : "pendientes" },
    ...(stats.blocked > 0
      ? [{ value: stats.blocked, label: stats.blocked === 1 ? "bloqueada" : "bloqueadas" }]
      : []),
  ];

  return (
    <Box sx={{ ...cardSx(theme), px: { xs: 2, sm: 3 }, py: 2.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1.75 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Typography sx={{ fontSize: "1.125rem", fontWeight: 500, color: "text.primary" }}>
            Progreso
          </Typography>
          {setupStatus ? (
            <StatusSelect value={setupState} statusMap={SETUP_STATUS} />
          ) : null}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
            {stats.completedPhases} de {phases.length} fases completadas
          </Typography>
          <Typography sx={{ fontSize: "1.5rem", fontWeight: 600, color: "primary.main" }}>
            {Math.round(projectProgress)}%
          </Typography>
        </Stack>
      </Stack>

      <Progress value={projectProgress} sx={{ "& .MuiLinearProgress-root": { height: 10 } }} />

      <Stack
        direction="row"
        flexWrap="wrap"
        columnGap={3}
        rowGap={0.5}
        sx={{ mt: 1.75, fontSize: "0.875rem", color: "text.secondary" }}
      >
        {statItems.map((item) => (
          <Box component="span" key={item.label}>
            <Box component="strong" sx={{ color: "text.primary", fontWeight: 500 }}>
              {item.value}
            </Box>{" "}
            {item.label}
          </Box>
        ))}
        {startLabel ? <Box component="span">Arranque: {startLabel}</Box> : null}
      </Stack>
    </Box>
  );
}

SetupProgressCard.propTypes = {
  phases: PropTypes.array.isRequired,
  projectProgress: PropTypes.number,
  setupState: PropTypes.string,
  startDate: PropTypes.string,
};

/**
 * Detalle del setup técnico, común a cliente y agencia: tarjeta de progreso y
 * lista de fases desplegables con sus tareas. Para la agencia los estados de fases y
 * tareas son editables desde la propia fila.
 */
export function SetupDetailView({
  phases,
  projectProgress = 0,
  setupState,
  startDate,
  isClient = false,
  onViewPhaseKanban,
  onUpdatePhaseStatus,
  onUpdateTaskStatus,
  onEditTask,
  onEditPhase,
  locked = false,
}) {
  // Todas las fases empiezan plegadas; el usuario abre las que quiera
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const togglePhase = (phaseId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) next.delete(phaseId);
      else next.add(phaseId);
      return next;
    });
  };

  return (
    <Stack spacing={2}>
      <SetupProgressCard
        phases={phases}
        projectProgress={projectProgress}
        setupState={setupState}
        startDate={startDate}
      />

      <Stack
        spacing={1.5}
        sx={locked ? { opacity: 0.55, pointerEvents: "none" } : undefined}
      >
        {phases.map((phase, index) => (
          <SetupPhaseRow
            key={phase.id}
            phase={phase}
            index={index}
            isExpanded={expandedIds.has(phase.id)}
            onToggle={() => togglePhase(phase.id)}
            isClient={isClient}
            onUpdatePhaseStatus={onUpdatePhaseStatus}
            onUpdateTaskStatus={onUpdateTaskStatus}
            onEditTask={onEditTask}
            onEditPhase={onEditPhase}
            onViewPhaseKanban={onViewPhaseKanban}
          />
        ))}
      </Stack>
    </Stack>
  );
}

SetupDetailView.propTypes = {
  phases: PropTypes.array.isRequired,
  projectProgress: PropTypes.number,
  setupState: PropTypes.string,
  startDate: PropTypes.string,
  isClient: PropTypes.bool,
  onViewPhaseKanban: PropTypes.func,
  onUpdatePhaseStatus: PropTypes.func,
  onUpdateTaskStatus: PropTypes.func,
  onEditTask: PropTypes.func,
  onEditPhase: PropTypes.func,
  locked: PropTypes.bool,
};

export default SetupDetailView;
